import { flow, types } from "mobx-state-tree";
import orderBy from "lodash/orderBy";
import $projects from "@/core/projects/store";
import { fetchData, saveData } from "@/core/api/translations";

const $translations = types
  .model("$translations", {
    data: types.array(
      types.model("$translations.item", {
        // код ru_RU, en_US
        code: types.string,
        // метка редактировался
        edited: types.maybe(types.boolean),
        // метка был создан
        created: types.maybe(types.boolean),
        // аккаумуляция всех слов используется для поиска
        query: types.maybe(types.string),
        // переводы
        translation: types.frozen({}),
      })
    ),
    active: types.maybe(types.string),
    // Метка изменялись переводы или нет
    changed: types.optional(types.boolean, false),
    search: types.maybe(types.string),
    revision: types.maybe(types.string),
  })
  .actions((self) => ({
    fetchData: flow(function* () {
      const project = $projects.getActive;
      if (!project) return;

      try {
        const { data } = yield fetchData({ projectId: project.id });
        self.setData(data);
      } catch (e) {
        // сообщение пользователю уже показал интерцептор
      }
    }),
    setData({ locales, revision }) {
      self.revision = revision;

      // Сервер отдаёт пары { id, translations }. Раскладываем по коду локали,
      // а не по индексу: сопоставление по позиции разъезжается, если реестр
      // локалей изменится между запросом проектов и запросом переводов.
      const byLocale = {};
      (locales || []).forEach(({ id, translations }) => {
        byLocale[id] = translations || {};
      });

      const result = {};
      ($projects.getLocales || []).forEach((locale) => {
        const values = byLocale[locale.id] || {};
        Object.keys(values).forEach((key) => {
          if (!result[key]) result[key] = {};
          result[key][locale.id] = values[key];
        });
      });

      self.data = Object.keys(result).map((code) => ({
        code,
        translation: result[code],
        query: self.getQuery({ code, ...result[code] }),
      }));
    },
    setSearch(value) {
      self.search = value;
    },
    save: flow(function* save() {
      try {
        const { data } = yield saveData({
          revision: self.revision,
          locales: self.buildTranslate,
        });
        self.revision = data.revision;
        self.changed = false;
      } catch (e) {
        // сообщение пользователю уже показал интерцептор
      }
    }),
    setActive(code) {
      self.active = code;
    },
    setTranslate(localeCode, value, keyCode) {
      const item = keyCode ? self.getById(keyCode) : self.getActive;
      if (!item) return;

      item.translation = { ...item.translation, [localeCode]: value };
      item.edited = true;
      item.query = self.getQuery({ code: item.code, ...item.translation });
      self.changed = true;
    },
    push(key, codes = {}) {
      if (self.getById(key) === undefined) {
        const translation = {};
        $projects.getLocales.forEach(({ id }) => {
          translation[id] = codes[id] || "";
        });
        self.data.push({
          code: key,
          translation,
          created: true,
          query: self.getQuery({ code: key, ...translation }),
        });
        return true;
      }
      return false;
    },
    destroy() {
      self.data = [];
      self.search = "";
      self.active = undefined;
      self.changed = false;
      self.revision = undefined;
    },
  }))
  .views((self) => ({
    getQuery(obj) {
      return Object.values(obj).join("|").toLowerCase();
    },
    get getData() {
      return self.data;
    },
    get filteredData() {
      if (self.search) {
        return self.getData.filter((n) =>
          n.query.includes(self.search.toLowerCase())
        );
      }
      return self.getData;
    },
    get getSortedData() {
      const data = self.filteredData;
      return orderBy(data, ["created", "code"]).slice(0, 500);
    },
    get getActive() {
      return self.getData.find((d) => d.code === self.active);
    },
    getById(id) {
      return self.getData.find((d) => d.code === id);
    },
    get getActiveMapped() {
      const active = self.getActive;
      if (active) {
        const result = {
          code: active.code,
          translation: [],
        };
        $projects.getLocales.forEach(({ id }) => {
          result.translation.push({
            code: id,
            value: active.translation[id] || "",
          });
        });
        return result;
      }

      return undefined;
    },
    get buildTranslate() {
      const result = {};
      self.getData.forEach(({ code, translation }) => {
        $projects.getLocales.forEach(({ id }) => {
          if (result[id] === undefined) result[id] = {};
          result[id][code] = translation[id];
        });
      });
      return result;
    },
  }))
  .create();

export default $translations;
